import React, { useRef, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import MobileChartWrapper from './MobileChartWrapper';
import MSTRMobileMainChartPage from './mstr/MSTRMobileMainChartPage';
import MobilePredictionsList from './MobilePredictionsList';
import MobileBottomNav, { FilterStatus } from './MobileBottomNav';
import BackToTopButton from './BackToTopButton';
import MobileInfluencerFilter from './MobileInfluencerFilter';
import { useLivePrice } from '../../hooks/useLivePrice';
import { Prediction } from '../../types/predictions';
import { Theme } from '../../theme';
import { formatPrediction } from '../../utils/priceUtils';

const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px; /* Reduced from 1px to 0px - elements touch each other */
  width: 100%;
  max-width: 100vw; /* Ensure container never exceeds viewport width */
  height: 100vh;
  padding: 1px 16px 80px 16px; /* Increased horizontal padding for full-width cards */
  box-sizing: border-box; /* Ensure padding is included in width */
  overflow-x: hidden; /* Prevent horizontal overflow */
  
  /* Improved scroll behavior */
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
  scroll-behavior: smooth;
  
  /* PWA mode - adjust for taller bottom nav */
  @media (display-mode: standalone) {
    padding-bottom: max(96px, 76px + env(safe-area-inset-bottom) + 20px);
  }
  
  /* Add minimum scrollable content to prevent scroll trap */
  &::after {
    content: '';
    height: 20px;
    width: 100%;
    flex-shrink: 0;
  }
  
  /* Ensure all child elements respect container bounds */
  & > * {
    max-width: 100%;
    box-sizing: border-box;
  }
`;

const PullToRefreshIndicator = styled.div<{ visible: boolean; pulling: boolean }>`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%) translateY(${props => props.visible ? '0' : '-100%'});
  background: ${props => props.theme.primary};
  color: white;
  padding: 8px 16px;
  border-radius: 0 0 8px 8px;
  font-size: 12px;
  font-weight: 600;
  z-index: 1000;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  
  ${props => props.pulling && `
    background: ${props.theme.primary};
    transform: translateX(-50%) translateY(0) scale(1.05);
  `}
`;

const AssetSelectionOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const AssetMenu = styled.div`
  background: ${props => props.theme.surface};
  border: 1px solid ${props => props.theme.border};
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 300px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`;

const AssetMenuHeader = styled.h3`
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  color: ${props => props.theme.text};
`;

const AssetOption = styled.button<{ active: boolean; theme: any }>`
  width: 100%;
  padding: 16px;
  margin-bottom: 12px;
  background: ${props => props.active ? props.theme.primary : props.theme.background};
  color: ${props => props.active ? 'white' : props.theme.text};
  border: 1px solid ${props => props.active ? props.theme.primary : props.theme.border};
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  text-align: left;
  
  &:hover {
    background: ${props => props.active ? props.theme.primary : props.theme.border};
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const AssetMenuClose = styled.button`
  width: 100%;
  padding: 12px;
  background: transparent;
  color: ${props => props.theme.text};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  opacity: 0.7;
  
  &:hover {
    opacity: 1;
    background: ${props => props.theme.border};
  }
`;

const BitcoinIcon = styled.svg`
  width: 40px;
  height: 40px;
  margin-right: 4px;
  flex-shrink: 0;
`;

const LivePriceBar = styled.div`
  width: 100%;
  height: 34px; /* Reduced from 40px to 34px */
  background: ${props => props.theme.surface};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px; /* Changed from 8px 8px 0 0 to disconnect from chart */
  padding: 4px 16px; /* Reduced from 6px to 4px */
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px; /* Reduced from 12px to 10px */
  box-sizing: border-box;
`;

const LivePriceValue = styled.div<{ isBlinking?: boolean }>`
  font-size: 18px; /* Reduced from 20px to 18px */
  font-weight: bold;
  color: ${props => props.theme.primary};
  font-family: 'Courier New', monospace;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  
  ${props => props.isBlinking && `
    animation: priceBlink 0.6s ease-in-out;
    
    @keyframes priceBlink {
      0% { opacity: 1; transform: scale(1); }
      30% { opacity: 0.8; transform: scale(1.02); }
      100% { opacity: 1; transform: scale(1); }
    }
  `}
`;

const LiveIndicator = styled.span<{ isLive: boolean }>`
  background: ${props => props.isLive ? '#22C55E' : '#6B7280'};
  color: white;
  padding: 2px 6px; /* Reduced from 4px 8px to 2px 6px */
  border-radius: 10px; /* Reduced from 12px to 10px */
  font-size: 8px; /* Reduced from 10px to 8px */
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px ${props => props.isLive ? 'rgba(34, 197, 94, 0.3)' : 'rgba(107, 114, 128, 0.3)'};
  transition: all 0.3s ease;
  
  ${props => props.isLive && `
    animation: livePulse 3s infinite;
    
    @keyframes livePulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.7; }
    }
  `}
`;

const MobileContestBanner = styled.div<{ $hasEntry: boolean; $expanded?: boolean }>`
  display: flex;
  flex-direction: ${props => props.$expanded ? 'column' : 'row'};
  align-items: ${props => props.$expanded ? 'stretch' : 'center'};
  justify-content: space-between;
  background: linear-gradient(90deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid ${props => props.$hasEntry ? '#4caf50' : '#f7931a'};
  border-radius: 8px;
  padding: 10px 14px;
  margin: 4px 0;
  gap: 8px;
`;

const ContestInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ContestTitle = styled.span`
  color: #f7931a;
  font-size: 13px;
  font-weight: 700;
`;

const ContestSubtitle = styled.span`
  color: #fff;
  font-size: 11px;
  opacity: 0.8;
`;

const ContestAction = styled.button<{ $isSuccess?: boolean }>`
  background: ${props => props.$isSuccess ? '#4caf50' : '#f7931a'};
  color: ${props => props.$isSuccess ? '#fff' : '#000'};
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:active {
    transform: scale(0.95);
  }
`;

const ContestInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-top: 8px;
`;

const ContestPriceInput = styled.input`
  flex: 1;
  background: rgba(255,255,255,0.1);
  border: 1px solid #f7931a;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 16px;
  color: #fff;
  text-align: center;
  
  &::placeholder {
    color: rgba(255,255,255,0.4);
  }
  
  &:focus {
    outline: none;
    border-color: #ffb84d;
  }
`;

const ContestSubmitBtn = styled.button`
  background: #f7931a;
  color: #000;
  border: none;
  border-radius: 6px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
`;

const ChartContainer = styled.div`
  flex: 0 0 260px; /* Reduced from 280px for more space for predictions */
  width: 100%;
  max-width: 100%; /* Prevent container from exceeding parent width */
  background: ${props => props.theme.surface};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px 8px 0 0; /* Rounded top corners, connect to predictions list below */
  overflow: hidden;
  overflow-x: hidden; /* Explicitly prevent horizontal overflow */
  box-sizing: border-box;
  position: relative;
  margin-top: 4px; /* Add small gap between live price and chart */
  
  /* Add scroll zone markers */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 20px;
    background: linear-gradient(180deg, ${props => props.theme.surface}aa 0%, transparent 100%);
    pointer-events: none;
    z-index: 10;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 20px;
    background: linear-gradient(0deg, ${props => props.theme.surface}aa 0%, transparent 100%);
    pointer-events: none;
    z-index: 10;
  }
`;

interface MobileMainChartPageProps {
  activePredictions: number[];
  togglePrediction: (id: number) => void;
  chartData: Array<{ time: string; value: number }>;
  isDarkMode: boolean;
  theme: Theme;
  predictions: Prediction[];
  onRefresh?: () => Promise<void>; // Optional refresh callback
  onResetChart?: () => void; // Optional chart reset callback
  openModal?: () => void; // Optional modal opener for submit functionality
  // ARCHIVED: Contest props removed (April 2026 contest ended)
  isLoggedIn?: boolean;
  userHandle?: string;
  // userContestPrediction?: number | null;
  // userContestEntry?: { price_prediction: number; submitted_at: string; id: number } | null;
  onLogin?: () => void;
  // onEntrySubmitted?: (price: number, entry?: any) => void;
}

const MobileMainChartPage: React.FC<MobileMainChartPageProps> = ({
  activePredictions,
  togglePrediction,
  chartData,
  isDarkMode,
  theme,
  predictions,
  onRefresh,
  onResetChart,
  openModal,
  // ARCHIVED: Contest params removed (April 2026 contest ended)
  isLoggedIn,
  userHandle,
  // userContestPrediction,
  // userContestEntry,
  onLogin,
  // onEntrySubmitted
}) => {
  const location = useLocation();
  
  // Refs for scroll management
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Bottom nav state
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');
  const [showMenu, setShowMenu] = useState(false);
  
  // Influencer filter state
  const [selectedInfluencer, setSelectedInfluencer] = useState<string>('all');
  
  // ARCHIVED: Contest input state removed (April 2026 contest ended)
  // const [showContestInput, setShowContestInput] = useState(false);
  // const [contestPrice, setContestPrice] = useState('');
  // const [isSubmittingContest, setIsSubmittingContest] = useState(false);

  // Determine current asset from route
  const currentAsset = React.useMemo(() => {
    if (location.pathname.includes('/mstr')) return 'mstr';
    if (location.pathname.includes('/naka')) return 'naka';
    return 'btc'; // default
  }, [location.pathname]);

  // Route to MSTR-specific mobile component when on MSTR route
  if (currentAsset === 'mstr') {
    return (
      <MSTRMobileMainChartPage
        activePredictions={activePredictions}
        togglePrediction={togglePrediction}
        chartData={chartData}
        isDarkMode={isDarkMode}
        theme={theme}
        predictions={predictions}
        openModal={openModal}
      />
    );
  }

  // Default Bitcoin mobile chart (existing implementation)
  const handlePredictionClick = (predictionId: number) => {
    // FAST PATH: Direct toggle first for immediate UI response
    togglePrediction(predictionId);
    
    // 🐰 EASTER EGG: Handle Easter Bunny asynchronously after UI update
    if (predictionId === 331) {
      setTimeout(() => {
        console.log('🐰 Easter Bunny clicked! Activating Easter egg hunt...');
        const easterEggEvent = new CustomEvent('activateEasterEgg', {
          detail: { mode: 'hunt', eggCount: 21 }
        });
        window.dispatchEvent(easterEggEvent);
      }, 0);
    }
  };

  // Bottom nav handlers
  const handleMenuClick = () => {
    setShowMenu(!showMenu);
  };

  const handleAssetSelect = (asset: 'btc' | 'mstr') => {
    setShowMenu(false);
    // Navigate to the selected asset
    if (asset === 'btc' && location.pathname !== '/btc') {
      window.location.href = '/btc';
    } else if (asset === 'mstr' && location.pathname !== '/mstr') {
      window.location.href = '/mstr';
    }
  };

  const handleResetChart = () => {
    
    // Reset local filter state
    setStatusFilter('all');
    
    // Call the parent's reset chart function to clear active predictions
    if (onResetChart) {
      onResetChart();
    } else {
      console.warn('⚠️ No onResetChart function provided');
    }
  };

  const handleStatusFilterChange = (filter: FilterStatus) => {
    setStatusFilter(filter);
  };

  // Handle influencer filter change
  const handleInfluencerChange = (influencer: string) => {
    setSelectedInfluencer(influencer);
  };

  // Extract unique influencers
  const uniqueInfluencers = React.useMemo(() => {
    const influencers = new Set<string>();
    predictions.forEach(pred => {
      if (pred.influencer?.name) {
        influencers.add(pred.influencer.name);
      }
    });
    return Array.from(influencers).sort();
  }, [predictions]);

  // Filter predictions based on status and influencer
  const filteredPredictions = React.useMemo(() => {
    let filtered = predictions;
    
    // Apply influencer filter
    if (selectedInfluencer !== 'all') {
      filtered = filtered.filter(prediction => 
        prediction.influencer?.name === selectedInfluencer
      );
    }
    
    // Apply status/type filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(prediction => {
        switch (statusFilter) {
          case 'success':
            return prediction.success_status === 'success';
          case 'miss':
            return prediction.success_status === 'miss';
          case 'pending':
            return prediction.success_status === 'pending' || !prediction.success_status;
          case 'updated':
            return prediction.success_status === 'updated';
          case 'bet':
            return prediction.prediction_type === 'bet';
          case 'sentiment':
            return prediction.prediction_type === 'sentiment';
          default:
            return true;
        }
      });
    }
    
    return filtered;
  }, [predictions, statusFilter, selectedInfluencer]);

  // Sort predictions by nearest upcoming target date (next to resolve first)
  const sortedPredictions = React.useMemo(() => {
    console.log('📱 Mobile: Sorting predictions by upcoming target date');
    
    const now = new Date();
    
    // First, separate Easter Bunny from regular predictions
    const easterBunny = filteredPredictions.find(p => p.id === 331);
    const regularPredictions = filteredPredictions.filter(p => p.id !== 331);
    
    const sorted = [...regularPredictions].sort((a, b) => {
      // Get target dates - use end_date as the "due date" for the prediction
      const aTargetDate = a.end_date ? new Date(a.end_date) : (a.target_time ? new Date(a.target_time) : null);
      const bTargetDate = b.end_date ? new Date(b.end_date) : (b.target_time ? new Date(b.target_time) : null);
      
      // Determine if predictions are pending (not yet resolved)
      const aIsPending = !a.success_status || a.success_status === 'pending';
      const bIsPending = !b.success_status || b.success_status === 'pending';
      
      // Pending predictions come before resolved ones
      if (aIsPending && !bIsPending) return -1;
      if (!aIsPending && bIsPending) return 1;
      
      // For pending predictions: sort by nearest upcoming date
      if (aIsPending && bIsPending) {
        if (!aTargetDate && !bTargetDate) return 0;
        if (!aTargetDate) return 1; // No date goes to end
        if (!bTargetDate) return -1;
        
        // Nearest upcoming date first
        return aTargetDate.getTime() - bTargetDate.getTime();
      }
      
      // For resolved predictions: most recently resolved first (by end_date descending)
      if (!aTargetDate && !bTargetDate) return 0;
      if (!aTargetDate) return 1;
      if (!bTargetDate) return -1;
      return bTargetDate.getTime() - aTargetDate.getTime(); // Descending for resolved
    });
    
    // 🐰 Insert Easter Bunny in the middle of the list (around position 15-20)
    if (easterBunny && sorted.length > 10) {
      const middlePosition = Math.floor(sorted.length * 0.6); // 60% through the list
      sorted.splice(middlePosition, 0, easterBunny);
    } else if (easterBunny) {
      // If fewer predictions, add at end
      sorted.push(easterBunny);
    }
    
    console.log('📱 Mobile: Sorted predictions, first 3:', sorted.slice(0, 3).map(p => ({ 
      id: p.id, 
      name: p.influencer?.name, 
      end_date: p.end_date,
      status: p.success_status 
    })));
    return sorted;
  }, [filteredPredictions]);

  // Count filtered predictions
  const filteredCount = React.useMemo(() => {
    if (statusFilter === 'all') return predictions.length;
    return filteredPredictions.length;
  }, [filteredPredictions, statusFilter, predictions]);

  // Use live price hook for real-time updates
  const { price: livePrice, loading: livePriceLoading, isBlinking } = useLivePrice();

  // Get current price from live data (preferred) or fall back to chart data
  const currentPrice = React.useMemo(() => {
    // Use live price if available
    if (livePrice !== null) return livePrice;
    
    // Fallback to latest chart data point
    if (!chartData || chartData.length === 0) return null;
    const latestPoint = chartData[chartData.length - 1];
    return latestPoint ? latestPoint.value : null;
  }, [livePrice, chartData]);

  // Determine if we're showing live data
  const isShowingLiveData = livePrice !== null && currentPrice === livePrice;

  // Format price with commas and proper currency
  const formatPrice = (price: number | null) => {
    if (price === null) return livePriceLoading ? 'Loading...' : '--';
    return `$${Math.round(price).toLocaleString()}`;
  };

  // ARCHIVED: handleContestSubmit removed (April 2026 contest ended)
  // const handleContestSubmit = async () => { ... };

  // ARCHIVED: formatContestInput removed (April 2026 contest ended)
  // const formatContestInput = (val: string) => { ... };

  return (
    <MobileContainer ref={containerRef}>
      {/* Global CSS to prevent horizontal scrolling */}
      <style>
        {`
          html, body {
            overflow-x: hidden !important;
            max-width: 100vw !important;
            box-sizing: border-box !important;
          }
          
          * {
            box-sizing: border-box !important;
          }
          
          #root {
            overflow-x: hidden !important;
            max-width: 100vw !important;
          }
        `}
      </style>
      
      {/* Asset Selection Menu */}
      {showMenu && (
        <AssetSelectionOverlay>
          <AssetMenu theme={theme}>
            <AssetMenuHeader>Select Asset</AssetMenuHeader>
            <AssetOption 
              active={currentAsset === 'btc'}
              theme={theme}
              onClick={() => handleAssetSelect('btc')}
              style={{
                justifyContent: 'center'
              }}
            >
              <BitcoinIcon viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="12" fill="#F7931A"/>
                <text x="12" y="16" textAnchor="middle" fontSize="14" fontWeight="bold" fill="white" fontFamily="Arial, sans-serif" transform="rotate(8 12 16)">₿</text>
              </BitcoinIcon>
              Bitcoin (BTC)
            </AssetOption>
            <AssetOption 
              active={false}
              theme={theme}
              onClick={() => {}}
              style={{ 
                opacity: 1, 
                cursor: 'not-allowed',
                background: '#f8f8f8',
                color: '#666666',
                flexDirection: 'column',
                textAlign: 'center',
                gap: '4px'
              }}
              disabled
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                📈 Strategy (MSTR)
              </div>
              <div style={{ fontSize: '12px', color: '#999999' }}>
                Coming Soon
              </div>
            </AssetOption>
            <AssetMenuClose onClick={() => setShowMenu(false)}>
              Cancel
            </AssetMenuClose>
          </AssetMenu>
        </AssetSelectionOverlay>
      )}
      
      <LivePriceBar theme={theme}>
        <LivePriceValue theme={theme} isBlinking={isBlinking}>
          {formatPrice(currentPrice)}
        </LivePriceValue>
        <LiveIndicator isLive={isShowingLiveData}>
          {isShowingLiveData ? 'LIVE' : 'STATIC'}
        </LiveIndicator>
      </LivePriceBar>
      
      {/* ARCHIVED: Mobile Contest Entry Banner removed (April 2026 contest ended) */}
      {/* <MobileContestBanner $hasEntry={!!userContestPrediction} $expanded={showContestInput}> ... </MobileContestBanner> */}
      
      <ChartContainer>
        <MobileChartWrapper
          predictions={predictions}
          activePredictions={activePredictions}
          chartData={chartData}
          isDarkMode={isDarkMode}
          theme={theme}
          // ARCHIVED: Contest props removed (April 2026 contest ended)
          // userContestPrediction={userContestPrediction}
          userHandle={userHandle}
        />
      </ChartContainer>
      
      <MobileInfluencerFilter
        theme={theme}
        selectedInfluencer={selectedInfluencer}
        uniqueInfluencers={uniqueInfluencers}
        onInfluencerChange={handleInfluencerChange}
        filteredCount={filteredPredictions.length}
        totalCount={predictions.length}
      />
      <MobilePredictionsList
        predictions={sortedPredictions}
        activePredictions={activePredictions}
        onPredictionClick={handlePredictionClick}
        openModal={openModal}
        currentAsset={currentAsset}
        // ARCHIVED: Contest props removed (April 2026 contest ended)
        // userContestPrediction={userContestPrediction}
        // userContestEntryDate={userContestEntry?.submitted_at}
        userHandle={userHandle}
      />
      
      <BackToTopButton
        theme={theme}
        containerRef={containerRef}
        scrollThreshold={200}
      />
      
      <MobileBottomNav
        theme={theme}
        onMenuClick={handleMenuClick}
        onResetChart={handleResetChart}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusFilterChange}
        filteredCount={filteredCount}
      />
    </MobileContainer>
  );
};

export default MobileMainChartPage;